import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Cadence d'interrogation de `table-session/<uuid>/` par le téléphone du convive.
 *
 * Audit temps réel du 2026-09-04 (DF-0152) : le convive sur place est le poste
 * de coût dominant de toute la plateforme. Quatre abonnés — TablePanel,
 * useSharedTableCart, useTableOrderingLock, useBuffetRoundGate — sondaient
 * chacun toutes les 5 s ; RTK Query les déduplique en UNE requête, mais c'est
 * quand même **720 requêtes par téléphone et par heure de service**, à ~40 ms de
 * CPU chacune sur le seul process daphne de `sale_api`. Environ **125
 * téléphones ouverts en même temps** saturaient un cœur, avant même de compter
 * les tablettes serveur, les tableaux de bord et la création des commandes ;
 * une salle de 60 couverts en consommait la moitié. Et quand ça sature, ce sont
 * les battements du flux d'impression — servis par la même boucle — qui prennent
 * du retard, donc les tickets de cuisine qui ne sortent plus.
 *
 * Les 5 s ne servent que pendant qu'il se passe quelque chose : le convive
 * ajoute au panier partagé, un voisin de table envoie une tournée, le serveur
 * encaisse. Un téléphone posé sur la table, écran allumé et personne dessus,
 * n'a pas besoin de la même cadence — et c'est le cas le plus fréquent d'un
 * service. La cadence s'essouffle donc depuis la dernière action du convive, et
 * **revient à 5 s dès qu'il touche l'écran** (avec un rafraîchissement
 * immédiat, puisque le compteur repart de zéro).
 *
 * On ne descend jamais à 0 tant que l'onglet est visible : une table reste
 * vivante, elle coûte seulement moins cher. `0` est réservé à l'onglet caché,
 * exactement comme avant (« Fix 11 »).
 */

/** Paliers cumulés depuis la dernière action du convive. */
export const TABLE_SESSION_POLL_STEPS: ReadonlyArray<{ untilMs: number; intervalMs: number }> = [
  { untilMs: 2 * 60_000, intervalMs: 5_000 },
  { untilMs: 10 * 60_000, intervalMs: 15_000 },
];

/** Cadence d'un téléphone visible mais inactif depuis longtemps. */
export const TABLE_SESSION_POLL_IDLE_MS = 30_000;

/** Cadence à cet instant, en ms. Ne rend jamais 0 — l'arrêt vient de la visibilité. */
export function tableSessionPollIntervalMs(msSinceActivity: number): number {
  const step = TABLE_SESSION_POLL_STEPS.find((candidate) => msSinceActivity < candidate.untilMs);
  return step ? step.intervalMs : TABLE_SESSION_POLL_IDLE_MS;
}

/** Délai avant le prochain changement de cadence, en ms. `null` au dernier palier. */
export function msUntilNextTableSessionPollStep(msSinceActivity: number): number | null {
  const next = TABLE_SESSION_POLL_STEPS.find((candidate) => msSinceActivity < candidate.untilMs);
  return next ? next.untilMs - msSinceActivity : null;
}

/** Gestes qui prouvent que quelqu'un est devant l'écran. */
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart'] as const;

/**
 * `pollingInterval` à passer à `useGetTableSessionQuery`. `0` = onglet caché.
 *
 * Remplace le trio `useState(!document.hidden)` + `visibilitychange` +
 * `isVisible ? 5000 : 0` qui était recopié dans les quatre abonnés.
 */
export function useTableSessionPollInterval(): number {
  const [isVisible, setIsVisible] = useState(
    typeof document === 'undefined' ? true : !document.hidden,
  );
  const [msSinceActivity, setMsSinceActivity] = useState(0);
  // La date de la dernière action vit dans une ref : la lire ne doit pas
  // reprogrammer le minuteur, seul le franchissement d'un palier le doit.
  const lastActivityAt = useRef(Date.now());

  const markActive = useCallback(() => {
    lastActivityAt.current = Date.now();
    setMsSinceActivity(0);
  }, []);

  useEffect(() => {
    const onVisibility = () => {
      const visible = !document.hidden;
      setIsVisible(visible);
      // Revenir sur l'onglet est une action : on veut l'état frais tout de suite.
      if (visible) markActive();
    };
    document.addEventListener('visibilitychange', onVisibility);
    for (const name of ACTIVITY_EVENTS) {
      document.addEventListener(name, markActive, { passive: true });
    }
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      for (const name of ACTIVITY_EVENTS) {
        document.removeEventListener(name, markActive);
      }
    };
  }, [markActive]);

  // Un seul minuteur à la fois, armé sur le prochain palier — pas un compteur
  // qui se réveille chaque seconde pour ne rien changer.
  useEffect(() => {
    if (!isVisible) return;
    const delay = msUntilNextTableSessionPollStep(msSinceActivity);
    if (delay === null) return;
    const id = window.setTimeout(() => {
      setMsSinceActivity(Date.now() - lastActivityAt.current);
    }, delay);
    return () => window.clearTimeout(id);
  }, [isVisible, msSinceActivity]);

  return isVisible ? tableSessionPollIntervalMs(msSinceActivity) : 0;
}
